import React, { useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Stack,
  Divider,
  CircularProgress,
  Alert,
  Sheet,
  Table,
  Tabs,
  TabList,
  Tab,
  TabPanel,
} from '@mui/joy'
import {
  ArrowLeft as ArrowBack,
  PencilSimple as Edit,
  Envelope as Email,
  Printer as Print,
  DownloadSimple as Download,
  ArrowsLeftRight as SwapHoriz,
  ClockCounterClockwise as History,
  CheckCircle,
  XCircle as Cancel,
} from '@phosphor-icons/react'
import { estimatesApi } from '../lib/billingApi'
import SendDocumentEmailModal from '../components/billing/SendDocumentEmailModal'
import { formatCurrency, formatStatus, getStatusColor } from '../lib/billingUtils'
import { formatDistanceToNow } from 'date-fns'
import type { BackendLineItem } from '../types/billing-backend'

function EstimateHistory({ estimateId }: { estimateId: number }) {
  const { data: history, isLoading } = useQuery({
    queryKey: ['estimate', estimateId, 'history'],
    queryFn: () => estimatesApi.history(estimateId),
    enabled: !!estimateId,
  })

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size="sm" />
      </Box>
    )
  }

  if (!history || history.length === 0) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Typography level="body-sm" sx={{ textAlign: 'center', color: 'text.tertiary' }}>
            No history recorded yet. Status changes will appear here.
          </Typography>
        </CardContent>
      </Card>
    )
  }

  return (
    <Stack spacing={2}>
      {history.map((entry) => (
        <Card key={entry.id} variant="outlined" size="sm">
          <CardContent sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5 }}>
                {entry.event_data?.new_status && (
                  <Chip
                    size="sm"
                    color={getStatusColor(entry.event_data.new_status) as any}
                    variant="soft"
                  >
                    {formatStatus(entry.event_data.new_status)}
                  </Chip>
                )}
                {entry.event_data?.old_status && (
                  <Typography level="body-xs" color="neutral">
                    from {formatStatus(entry.event_data.old_status)}
                  </Typography>
                )}
              </Box>
              <Typography level="body-sm" fontWeight="md">
                {entry.event_title}
              </Typography>
            </Box>
            <Typography level="body-xs" color="neutral" sx={{ whiteSpace: 'nowrap' }}>
              {formatDistanceToNow(new Date(entry.timestamp), { addSuffix: true })}
            </Typography>
          </CardContent>
        </Card>
      ))}
    </Stack>
  )
}

export default function EstimateDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showEmailDialog, setShowEmailDialog] = useState(false)

  // Fetch estimate details
  const { data: estimate, isLoading, error } = useQuery({
    queryKey: ['estimate', id],
    queryFn: () => estimatesApi.get(Number(id)),
    enabled: !!id,
  })
  
  // Convert to invoice mutation
  const convertMutation = useMutation({
    mutationFn: (force: boolean = false) => estimatesApi.convertToInvoice(Number(id), force),
    onSuccess: (data) => {
      // The server offers a material order when the estimate has material
      // lines. OrderCreate reads this from sessionStorage; nothing ever wrote
      // it, so the auto-mapping it was built around could never run.
      if (data.suggest_material_order && data.material_order_data) {
        sessionStorage.setItem('materialOrderData', JSON.stringify(data.material_order_data))
      }
      // Backend returns { status, invoice: {...} }; fall back to legacy invoice_id.
      const invoiceId = data.invoice?.id ?? data.invoice_id
      navigate(`../invoices/${invoiceId}`)
    },
  })

  /**
   * Convert, asking first if the estimate has not been accepted.
   *
   * Contractors routinely invoice from an estimate accepted over the phone, so
   * the unaccepted case is a confirmation rather than a refusal.
   */
  const handleConvertToInvoice = () => {
    const isAccepted = estimate?.acceptance_status === 'accepted' || estimate?.status === 'accepted'
    if (!isAccepted) {
      const proceed = window.confirm(
        'This estimate has not been marked as accepted. Convert it to an invoice anyway?',
      )
      if (!proceed) return
    }
    convertMutation.mutate(!isAccepted)
  }
  
  // Generate display line items including overhead and profit
  const getDisplayLineItems = () => {
    if (!estimate) return []
    
    const items: (BackendLineItem & { is_system_generated?: boolean })[] = (estimate.line_items || []).map(item => ({
      ...item,
      item_type: item.item_type || 'service',
      is_taxable: item.is_taxable ?? false,
      sort_order: item.sort_order || 0,
      quantity: item.quantity,
      unit_price: item.unit_price,
      tax_rate: item.tax_rate || null,
      total: item.total,
      description: item.description,
      notes: item.notes,
      is_bold: item.is_bold
    }))
    
    // Add overhead as a line item if included
    if (estimate.include_overhead && estimate.overhead_amount && parseFloat(estimate.overhead_amount) > 0) {
      items.push({
        description: `Overhead (${estimate.overhead_percentage}%)`,
        notes: 'Calculated based on subtotal',
        quantity: 1,
        unit_price: estimate.overhead_amount,
        item_type: 'other',
        tax_rate: null,
        is_taxable: false,
        total: estimate.overhead_amount,
        sort_order: 9998,
        is_system_generated: true,
      })
    }
    
    // Add profit as a line item if included
    if (estimate.include_profit && estimate.profit_amount && parseFloat(estimate.profit_amount) > 0) {
      items.push({
        description: `Profit (${estimate.profit_percentage}%)`,
        notes: 'Calculated based on subtotal',
        quantity: 1,
        unit_price: estimate.profit_amount,
        item_type: 'other',
        tax_rate: null,
        is_taxable: false,
        total: estimate.profit_amount,
        sort_order: 9999,
        is_system_generated: true,
      })
    }
    
    return items
  }
  
  // PDF download mutation
  const downloadPdfMutation = useMutation({
    mutationFn: () => estimatesApi.generatePdf(Number(id)),
    onSuccess: (data) => {
      // Create a temporary link element to download the file
      const link = document.createElement('a')
      link.href = data.file_url
      link.download = `estimate_${estimate?.document_number || id}.pdf`
      link.target = '_blank'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
  })
  
  // Mark as sent mutation
  const markSentMutation = useMutation({
    mutationFn: () => estimatesApi.update(Number(id), { status: 'sent' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['estimate', id] })
    },
  })
  
  
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }
  
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    )
  }
  
  if (error || !estimate) {
    return (
      <Alert color="danger" sx={{ m: 2 }}>
        Error loading estimate. Please try again.
      </Alert>
    )
  }
  
  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button
          component={Link}
          to="/operations/billing/estimates"
          startDecorator={<ArrowBack />}
          variant="plain"
          size="sm"
          sx={{ mb: 2 }}
        >
          Back to Estimates
        </Button>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography level="h1" sx={{ mb: 1 }}>
              Estimate {estimate.document_number}
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <Chip
                size="lg"
                color={getStatusColor(estimate.status)}
                variant="soft"
              >
                {formatStatus(estimate.status)}
              </Chip>
              {estimate.is_expired && (
                <Chip
                  size="sm"
                  color="warning"
                  variant="outlined"
                  startDecorator={<Cancel />}
                >
                  Expired
                </Chip>
              )}
              {estimate.acceptance_status === 'accepted' && (
                <Chip
                  size="sm"
                  color="success"
                  variant="outlined"
                  startDecorator={<CheckCircle />}
                >
                  Accepted
                </Chip>
              )}
            </Stack>
          </Box>

          <Stack direction="row" spacing={1}>
            {estimate.status === 'draft' && (
              <Button
                variant="outlined"
                color="primary"
                onClick={() => markSentMutation.mutate()}
                loading={markSentMutation.isPending}
              >
                Mark as Sent
              </Button>
            )}
            {/*
              One conversion path, server-side.

              There used to be a second, always-rendered "Convert To Invoice"
              button that hand-built the payload in the browser: it hardcoded
              item_type 'service' and is_taxable false on every line, dropped
              notes, sub-items, taxable_pct and is_bold, and sent no discount
              or overhead/profit flags. A taxable, discounted estimate with
              overhead and profit converted to an invoice for materially less
              money. estimatesApi.convertToInvoice copies all of it.
            */}
            {!estimate.converted_to_invoice && (
              <Button
                variant="outlined"
                color="success"
                startDecorator={<SwapHoriz />}
                onClick={() => handleConvertToInvoice()}
                loading={convertMutation.isPending}
              >
                Convert to Invoice
              </Button>
            )}
            <Button
              variant="outlined"
              color="primary"
              startDecorator={<Email />}
              onClick={() => setShowEmailDialog(true)}
            >
              Send Email
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startDecorator={<Print />}
              onClick={() => window.print()}
            >
              Print
            </Button>
            <Button
              component={Link}
              to={`/operations/billing/estimates/${id}/edit`}
              variant="solid"
              startDecorator={<Edit />}
            >
              Edit
            </Button>
          </Stack>
        </Box>
      </Box>
      
      {/* Main Content */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '3fr 1fr' }, gap: 3 }}>
        <Box>
          <Tabs defaultValue={0}>
            <TabList>
              <Tab>Details</Tab>
              <Tab>Items</Tab>
              <Tab>History</Tab>
            </TabList>
            
            <TabPanel value={0}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                    <Box>
                      <Typography level="title-sm" sx={{ mb: 1 }}>Customer</Typography>
                      <Typography level="body-md">
                        {estimate.customer_detail 
                          ? `${estimate.customer_detail.first_name || ''} ${estimate.customer_detail.last_name || ''}`.trim() || estimate.customer_detail.email || 'N/A'
                          : estimate.contact_name || 'N/A'}
                      </Typography>
                      {estimate.job_name && (
                        <Typography level="body-sm" color="neutral">
                          Job: {estimate.job_name}
                        </Typography>
                      )}
                    </Box>
                    
                    <Box>
                      <Typography level="title-sm" sx={{ mb: 1 }}>Dates</Typography>
                      <Typography level="body-sm">
                        Created: {formatDate(estimate.document_date)}
                      </Typography>
                      {estimate.valid_until && (
                        <Typography level="body-sm">
                          Valid Until: {formatDate(estimate.valid_until)}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  
                  {estimate.notes && (
                    <Box sx={{ mt: 3 }}>
                      <Typography level="title-sm" sx={{ mb: 1 }}>Notes</Typography>
                      <Typography level="body-sm">{estimate.notes}</Typography>
                    </Box>
                  )}
                  
                  {estimate.terms && (
                    <Box sx={{ mt: 3 }}>
                      <Typography level="title-sm" sx={{ mb: 1 }}>Terms & Conditions</Typography>
                      <Typography level="body-sm" sx={{ whiteSpace: 'pre-line' }}>
                        {estimate.terms}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </TabPanel>
            
            <TabPanel value={1}>
              <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'hidden' }}>
                <Table>
                  <thead>
                    <tr>
                      <th>Description</th>
                      <th style={{ width: 100 }}>Quantity</th>
                      <th style={{ width: 120 }}>Rate</th>
                      <th style={{ width: 120 }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getDisplayLineItems().length > 0 ? (
                      getDisplayLineItems().map((item, index) => (
                        <React.Fragment key={index}>
                          <tr
                            style={{
                              backgroundColor: item.is_system_generated ? '#f5f5f5' : 'transparent'
                            }}
                          >
                            <td>
                              <Box>
                                <Typography
                                  level="body-sm"
                                  fontWeight={item.is_bold ? 'bold' : 'normal'}
                                  sx={{
                                    fontStyle: item.is_system_generated ? 'italic' : 'normal'
                                  }}
                                >
                                  {item.description}
                                </Typography>
                                {item.notes && (
                                  <Typography
                                    level="body-xs"
                                    sx={{
                                      mt: 0.5,
                                      color: 'text.secondary',
                                      fontStyle: 'italic'
                                    }}
                                  >
                                    {item.notes}
                                  </Typography>
                                )}
                              </Box>
                            </td>
                            <td>
                              <Typography
                                level="body-sm"
                                fontWeight={item.is_bold ? 'bold' : 'normal'}
                                sx={{
                                  fontStyle: item.is_system_generated ? 'italic' : 'normal'
                                }}
                              >
                                {item.quantity}
                              </Typography>
                            </td>
                            <td>
                              <Typography
                                level="body-sm"
                                fontWeight={item.is_bold ? 'bold' : 'normal'}
                                sx={{
                                  fontStyle: item.is_system_generated ? 'italic' : 'normal'
                                }}
                              >
                                {formatCurrency(item.unit_price)}
                              </Typography>
                            </td>
                            <td>
                              <Typography
                                level="body-sm"
                                fontWeight={item.is_bold ? 'bold' : 'lg'}
                                sx={{
                                  fontStyle: item.is_system_generated ? 'italic' : 'normal'
                                }}
                              >
                                {formatCurrency(item.total)}
                              </Typography>
                            </td>
                          </tr>
                          {/* Internal sub-line-items (org-only, never shown to customer) */}
                          {(item.sub_items ?? []).map((sub, subIndex) => (
                            <tr
                              key={`sub-${index}-${subIndex}`}
                              style={{ backgroundColor: '#fafafa' }}
                            >
                              <td style={{ paddingLeft: 32 }}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <Chip size="sm" variant="soft" color="neutral">Internal</Chip>
                                  <Typography level="body-xs" textColor="text.secondary">
                                    {sub.description}
                                  </Typography>
                                </Stack>
                              </td>
                              <td>
                                <Typography level="body-xs" textColor="text.secondary">
                                  {sub.quantity}
                                </Typography>
                              </td>
                              <td>
                                <Typography level="body-xs" textColor="text.secondary">
                                  {formatCurrency(sub.unit_price)}
                                </Typography>
                              </td>
                              <td>
                                <Typography level="body-xs" textColor="text.secondary">
                                  {formatCurrency(sub.total)}
                                </Typography>
                              </td>
                            </tr>
                          ))}
                        </React.Fragment>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={4} style={{ textAlign: 'center' }}>
                          <Typography level="body-sm" color="neutral">
                            No line items
                          </Typography>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </Sheet>
              
              <Card variant="soft" sx={{ mt: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography level="body-md">Subtotal</Typography>
                    <Typography level="body-md">{formatCurrency(estimate.subtotal || 0)}</Typography>
                  </Box>
                  {estimate.tax_amount && parseFloat(estimate.tax_amount) > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography level="body-md">Tax</Typography>
                      <Typography level="body-md">{formatCurrency(estimate.tax_amount)}</Typography>
                    </Box>
                  )}
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography level="title-lg">Total</Typography>
                    <Typography level="title-lg" fontWeight="lg">
                      {formatCurrency(estimate.total_amount || 0)}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </TabPanel>
            
            <TabPanel value={2}>
              <EstimateHistory estimateId={Number(id)} />
            </TabPanel>
          </Tabs>
        </Box>
        
        {/* Sidebar */}
        <Box>
          <Stack spacing={3}>
            <Card variant="outlined">
              <CardContent>
                <Typography level="title-md" sx={{ mb: 2 }}>Summary</Typography>
                <Stack spacing={2}>
                  <Box>
                    <Typography level="body-sm" color="neutral">Total Amount</Typography>
                    <Typography level="h3">{formatCurrency(estimate.total_amount || 0)}</Typography>
                  </Box>
                  {estimate.days_until_expiry !== undefined && (
                    <Box>
                      <Typography level="body-sm" color="neutral">Valid For</Typography>
                      <Typography level="body-lg">
                        {estimate.days_until_expiry > 0 
                          ? `${estimate.days_until_expiry} days`
                          : 'Expired'
                        }
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>
            
            <Card variant="outlined">
              <CardContent>
                <Typography level="title-md" sx={{ mb: 2 }}>Actions</Typography>
                <Stack spacing={1}>
                  <Button
                    fullWidth
                    variant="outlined"
                    color="primary"
                    startDecorator={<Download />}
                    size="sm"
                    onClick={() => downloadPdfMutation.mutate()}
                    loading={downloadPdfMutation.isPending}
                  >
                    Download PDF
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    color="primary"
                    startDecorator={<History />}
                    size="sm"
                  >
                    View History
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Box>
      </Box>
      
      <SendDocumentEmailModal
        open={showEmailDialog}
        kind="estimate"
        documentId={id ? Number(id) : null}
        onClose={() => setShowEmailDialog(false)}
        onSent={() => queryClient.invalidateQueries({ queryKey: ['estimate', id] })}
      />
    </Box>
  )
}